import {inputAnatomy as parts} from '@chakra-ui/anatomy';
import {getToken} from '@chakra-ui/react';
import type {StyleFunctionProps} from '@chakra-ui/styled-system';
import {createMultiStyleConfigHelpers, cssVar, defineStyle} from '@chakra-ui/styled-system';
import {getColorVar, mode} from '@chakra-ui/theme-tools';

import {tokens as darkTokens} from '../../../generated/dark';
import {tokens as lightTokens} from '../../../generated/light';

import {vars} from './css-vars';

const {light, dark} = {
  light: lightTokens.components[`form-control`],
  dark: darkTokens.components[`form-control`],
};

const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers(parts.keys);
const {semantic} = lightTokens;
const {semantic: darkSemantic} = darkTokens;
export const $height = cssVar(`input-height`);
export const $fontSize = cssVar(`input-font-size`);
export const $padding = cssVar(`input-padding`);

const baseStyle = definePartsStyle((props: StyleFunctionProps) => ({
  field: {
    width: `full`,
    height: $height.reference,
    fontSize: $fontSize.reference,
    px: $padding.reference,
    borderRadius: vars[`border-radius`].reference,
    minWidth: 0,
    outline: 0,
    position: `relative`,
    appearance: `none`,
    [`&`]: {
      borderStyle: `solid`,
      borderWidth: `1px`,
      borderColor: mode(light[`stroke-default`], dark[`stroke-default`])(props),
    },
    transitionProperty: `common`,
    transitionDuration: `normal`,
    _disabled: {
      opacity: 1,
      cursor: `not-allowed`,
    },
    _placeholder: {
      color: mode(light[`text-placeholder`], dark[`text-placeholder`])(props),
    },
    _hover: {
      borderColor: mode(light[`stroke-hover`], dark[`stroke-hover`])(props),
    },
    _groupHover: {
      borderColor: mode(light[`stroke-hover`], dark[`stroke-hover`])(props),
    },
    _active: {
      borderColor: mode(light[`stroke-pressed`], dark[`stroke-pressed`])(props),
    },
    _groupActive: {
      borderColor: mode(light[`stroke-pressed`], dark[`stroke-pressed`])(props),
    },
    _focusVisible: {
      zIndex: 1,
      borderColor: mode(light[`stroke-focus`], dark[`stroke-focus`])(props),
    },
    _groupFocusWithin: {
      borderColor: mode(light[`stroke-focus`], dark[`stroke-focus`])(props),
    },

    _invalid: {
      borderColor: mode(
        semantic.colors.stroke.state[`danger-subtle`],
        darkSemantic.colors.stroke.state[`danger-subtle`],
      )(props),
      background: mode(
        semantic.colors.background.state[`danger-subtle`],
        darkSemantic.colors.background.state[`danger-subtle`],
      )(props),
      color: mode(light[`text-error`], dark[`text-error`])(props),
    },
  },
  addon: {
    height: $height.reference,
    fontSize: $fontSize.reference,
    px: $padding.reference,
    borderRadius: vars[`border-radius`].reference,
    color: mode(light[`icon-default`], dark[`icon-default`])(props),
    _groupHover: {
      color: mode(light[`icon-default`], dark[`icon-default`])(props),
      borderColor: mode(light[`stroke-hover`], dark[`stroke-hover`])(props),
    },
    _hover: {
      color: mode(light[`icon-default`], dark[`icon-default`])(props),
      borderColor: mode(light[`stroke-hover`], dark[`stroke-hover`])(props),
    },
    _groupActive: {
      color: mode(light[`icon-default`], dark[`icon-default`])(props),
      borderColor: mode(light[`stroke-pressed`], dark[`stroke-pressed`])(props),
    },
    _active: {
      color: mode(light[`icon-default`], dark[`icon-default`])(props),
      borderColor: mode(light[`stroke-pressed`], dark[`stroke-pressed`])(props),
    },
  },
}));

const size = {
  sm: defineStyle({
    [$fontSize.variable]: `fontSizes.sm`,
    [$padding.variable]: `space.space-100`,
    [vars[`border-radius`].variable]: `radii.radius-050`,
    [$height.variable]: `1.5rem`, // 24px
  }),
  lg: defineStyle({
    [$fontSize.variable]: `fontSizes.md`,
    [$padding.variable]: `space.space-150`,
    [vars[`border-radius`].variable]: `radii.radius-100`,
    [$height.variable]: `sizes.12`, // 48px
  }),
  md: defineStyle({
    [$fontSize.variable]: `fontSizes.sm`,
    [$padding.variable]: `space.space-100`,
    [vars[`border-radius`].variable]: `radii.radius-050`,
    [$height.variable]: `sizes.8`, // 32px
  }),
};

const sizes = {
  lg: definePartsStyle({
    field: size.lg,
    group: size.lg,
  }),
  md: definePartsStyle({
    field: size.md,
    group: size.md,
  }),
  sm: definePartsStyle({
    field: size.sm,
    group: size.sm,
  }),
};

const variantOutline = definePartsStyle(props => {
  const focusBorderToken = mode(light[`stroke-focus`], dark[`stroke-focus`])(props);
  const focusBorderColor = getToken(`colors`, focusBorderToken)(props.theme);

  return {
    field: {
      borderWidth: `1px`,
      borderStyle: `solid`,
      color: mode(light[`text-default`], dark[`text-default`])(props),
      borderColor: mode(light[`stroke-default`], dark[`stroke-default`])(props),
      backgroundColor: mode(light[`background-default`], dark[`background-default`])(props),

      _readOnly: {
        boxShadow: `none !important`,
        userSelect: `all`,
      },
      _invalid: {
        color: mode(light[`text-error`], dark[`text-error`])(props),
        borderColor: mode(light[`stroke-error`], dark[`stroke-error`])(props),
        boxShadow: `none`,
        backgroundColor: getColorVar(props.theme, `background.state.danger-subtle`),
      },
      _focusVisible: {
        zIndex: 1,
        borderColor: focusBorderToken,
        boxShadow: `0 0 0 1px ${focusBorderColor}`,
      },
    },
    addon: {
      borderWidth: `1px`,
      borderColor: mode(light[`stroke-default`], dark[`stroke-default`])(props),
      boxShadow: `none`,
      bg: mode(light[`background-default`], dark[`background-default`])(props),
    },
  };
});

const variantPlaceholder = definePartsStyle(props => {
  return {
    field: {
      // Border
      borderWidth: `1px`,
      borderColor: mode(light[`stroke-default`], dark[`stroke-default`])(props),
      borderStyle: `dashed`,

      // Pointer
      cursor: `pointer`,
      pointerEvents: `all`,
      transition: `0.1s linear`,

      _focusVisible: {
        borderStyle: `solid`,
      },
    },

    addon: {
      borderWidth: `1px`,
      borderStyle: `dashed`,
      borderColor: mode(light[`stroke-default`], dark[`stroke-default`])(props),
      boxShadow: `none`,
      color: mode(light[`icon-disabled`], dark[`icon-disabled`])(props),
      bg: `background.glass.1`,
    },
  };
});

const variants = {
  outline: variantOutline,
  placeholder: variantPlaceholder,
};

export const inputTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants,
  defaultProps: {
    size: `lg`,
    variant: `outline`,
  },
});
